<script setup lang="ts">
// Task shortcuts under the hero: each card is one thing a newcomer usually
// wants to do next (sign in, catch lots ending soon, shop on a budget, learn
// how escrow protects them, browse, sell). The whole card is the link.
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { UserRound, Clock, Wallet, ShieldCheck, LayoutGrid, PackagePlus, ArrowRight } from 'lucide-vue-next'
import { useUserStore } from '@/stores/user'
import { useI18n } from '@/composables/useI18n'

const userStore = useUserStore()
const { t } = useI18n()

const cards = computed(() => [
  userStore.isAuthenticated
    ? { id: 'account', to: '/dashboard/overview', icon: UserRound, title: t('homeDiscovery.cabinetTitle'), desc: t('homeDiscovery.cabinetDesc'), cta: t('homeDiscovery.cabinetCta') }
    : { id: 'account', to: '/login', icon: UserRound, title: t('homeDiscovery.accountTitle'), desc: t('homeDiscovery.accountDesc'), cta: t('homeDiscovery.accountCta') },
  { id: 'ending', to: '/auctions?sort=ending_soon&status=live', icon: Clock, title: t('homeDiscovery.endingTitle'), desc: t('homeDiscovery.endingDesc'), cta: t('homeDiscovery.endingCta') },
  { id: 'budget', to: '/auctions?maxPrice=10000', icon: Wallet, title: t('homeDiscovery.budgetTitle'), desc: t('homeDiscovery.budgetDesc'), cta: t('homeDiscovery.budgetCta') },
  { id: 'escrow', to: '/how-it-works', icon: ShieldCheck, title: t('homeDiscovery.escrowTitle'), desc: t('homeDiscovery.escrowDesc'), cta: t('homeDiscovery.escrowCta') },
  { id: 'categories', to: '/categories', icon: LayoutGrid, title: t('homeDiscovery.categoriesTitle'), desc: t('homeDiscovery.categoriesDesc'), cta: t('homeDiscovery.categoriesCta') },
  { id: 'sell', to: '/sell', icon: PackagePlus, title: t('homeDiscovery.sellTitle'), desc: t('homeDiscovery.sellDesc'), cta: t('landing.sellCta') },
])
</script>

<template>
  <section aria-labelledby="quick-links-title">
    <h2 id="quick-links-title" class="sr-only">{{ t('homeDiscovery.quickTitle') }}</h2>
    <ul class="rail lg:mx-0 lg:grid lg:grid-cols-6 lg:gap-3 lg:overflow-visible lg:px-0">
      <li v-for="card in cards" :key="card.id" class="w-[172px] shrink-0 sm:w-[190px] lg:w-auto">
        <RouterLink
          :to="card.to"
          class="group flex h-full flex-col rounded-card border border-border bg-surface p-4 text-center transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <span class="text-[13px] font-semibold leading-snug text-text-primary">{{ card.title }}</span>
          <span class="mx-auto my-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-primary transition group-hover:bg-primary/10">
            <component :is="card.icon" class="h-6 w-6" />
          </span>
          <span class="flex-1 text-[12px] leading-relaxed text-text-secondary">{{ card.desc }}</span>
          <span class="mt-3 inline-flex items-center justify-center gap-1 rounded-lg bg-primary/10 px-2 py-1.5 text-[12px] font-semibold text-primary-hover transition group-hover:bg-primary group-hover:text-onPrimary">
            {{ card.cta }}<ArrowRight class="h-3.5 w-3.5" />
          </span>
        </RouterLink>
      </li>
    </ul>
  </section>
</template>
